import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { predictionsService } from "@/api/services";
import { Trophy, Timer, CheckCircle, XCircle } from "@phosphor-icons/react";

function formatCountdown(ms) {
    if (ms <= 0) return null;
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    if (days > 0)
        return `${days}D ${String(hours).padStart(2, "0")}H ${String(minutes).padStart(2, "0")}M`;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function PickOfTheDay() {
    const [pick, setPick] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [now, setNow] = useState(Date.now());
    const intervalRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await predictionsService.featured();
                if (!cancelled) setPick(data?.pick || null);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("Featured pick load failed:", err?.message || err);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        intervalRef.current = setInterval(() => setNow(Date.now()), 1000);
        return () => {
            cancelled = true;
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    if (!loaded || !pick) return null;

    // Showcase mode = we're rendering a historical winning pick (because today
    // had no fixtures yet). The kickoff time is in the past, so we render a
    // result panel instead of a countdown timer.
    const showcase = !!pick.showcase;
    const status = pick.resultStatus || "pending";
    const score = pick.resultScore || null;

    const kickoffMs = pick.kickoff ? new Date(pick.kickoff).getTime() : null;
    const countdown = !showcase && kickoffMs ? formatCountdown(kickoffMs - now) : null;
    const isLive = !showcase && kickoffMs && kickoffMs <= now && status === "pending";

    const headerLabel = showcase ? "Recent Win" : "Pick of the day";
    const ctaLabel = showcase ? "See today's slip →" : "Get this slip →";

    return (
        <section
            className="max-w-7xl mx-auto px-4 md:px-8 mt-12 mb-16 relative"
            data-testid="pick-of-the-day"
        >
            <div className="bg-card border border-primary/40 neon-border relative overflow-hidden">
                <div className="absolute top-0 right-0 px-3 py-1 bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-widest font-bold">
                    {headerLabel}
                </div>
                <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-2 mb-3">
                            <Trophy size={18} weight="fill" className="text-primary" />
                            <span className="font-mono text-[10px] uppercase tracking-widest text-primary">
                                {showcase
                                    ? `Yesterday's top pick · Tier ${pick.category}`
                                    : `Highest confidence today · Tier ${pick.category}`}
                            </span>
                        </div>
                        <h3
                            className="text-2xl md:text-4xl font-black tracking-tight text-white mb-1"
                            data-testid="featured-match"
                        >
                            {pick.match}
                        </h3>
                        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-5">
                            {pick.league}
                            {pick.leagueCountry ? ` · ${pick.leagueCountry}` : ""}
                        </p>
                        <div className="flex flex-wrap gap-6 items-baseline">
                            <Stat label="Pick" value={pick.prediction} accent />
                            <Stat label="Market" value={pick.market} small />
                            <Stat label="Odds" value={Number(pick.odds || 0).toFixed(2)} mono />
                            <Stat label="Confidence" value={`${pick.confidence}%`} mono accent />
                        </div>
                    </div>
                    <div className="md:col-span-5 md:border-l md:border-border md:pl-8">
                        {showcase ? (
                            <ResultPanel status={status} score={score} />
                        ) : (
                            <CountdownPanel countdown={countdown} isLive={isLive} />
                        )}
                        <Link to={showcase ? "/dashboard" : "/register"}>
                            <Button
                                className="w-full rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                                data-testid="featured-cta"
                            >
                                {ctaLabel}
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function CountdownPanel({ countdown, isLive }) {
    return (
        <>
            <div className="flex items-center gap-2 mb-2">
                <Timer size={16} weight="bold" className="text-zinc-400" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Kicks off in
                </span>
            </div>
            <div
                className="font-mono text-3xl md:text-4xl font-bold mb-5"
                data-testid="featured-countdown"
            >
                {countdown ? (
                    <span className="text-primary neon-text">{countdown}</span>
                ) : isLive ? (
                    <span className="text-red-500 inline-flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full bg-red-500 live-dot" />
                        KICKED OFF
                    </span>
                ) : (
                    <span className="text-zinc-500">TBD</span>
                )}
            </div>
        </>
    );
}

function ResultPanel({ status, score }) {
    const won = status === "won";
    const lost = status === "lost";
    const Icon = won ? CheckCircle : lost ? XCircle : Trophy;
    const tone = won ? "text-primary" : lost ? "text-red-500" : "text-zinc-400";
    const label = won ? "Won" : lost ? "Lost" : status === "void" ? "Void" : "Settled";
    return (
        <>
            <div className="flex items-center gap-2 mb-2">
                <Icon size={16} weight="fill" className={tone} />
                <span className={`font-mono text-[10px] uppercase tracking-widest ${tone}`}>
                    Final result
                </span>
            </div>
            <div className="font-mono text-3xl md:text-4xl font-bold mb-1" data-testid="featured-result">
                {score && score.home !== null && score.home !== undefined ? (
                    <span className={tone}>
                        {score.home} : {score.away}
                    </span>
                ) : (
                    <span className={tone}>{label}</span>
                )}
            </div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-5">
                {won ? "Slip cashed" : lost ? "Better luck next time" : "Settled"}
            </p>
        </>
    );
}

function Stat({ label, value, accent, mono, small }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {label}
            </div>
            <div
                className={`${mono ? "font-mono" : ""} ${small ? "text-sm" : "text-lg"} font-semibold ${accent ? "text-primary" : "text-white"}`}
            >
                {value}
            </div>
        </div>
    );
}
